/**
 * SessionCard - Mantine-based session card for Monitored grid
 *
 * Displays session name, status, health, mute toggle, and unmonitor button
 */

import { useState, useEffect } from 'react'
import { Card, Text, Group, Badge, ActionIcon, Stack, ThemeIcon } from '@mantine/core'
import { IconVolume, IconVolumeOff, IconX } from '@tabler/icons-react'
import {
  type SessionInfo,
  getPrimaryColor,
  getPrimaryIcon,
  getHealthColor,
  getDetailText
} from '@session-tools/monitor-ui-core'

/**
 * ElapsedTime - Shows elapsed seconds since last change
 * Self-updates every second without re-rendering parent
 */
function ElapsedTime({ since }: { since: number | undefined }) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!since) return

    // Initial calculation
    setElapsed(Math.floor((Date.now() - since) / 1000))

    // Update every second
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - since) / 1000))
    }, 1000)

    return () => clearInterval(interval)
  }, [since])

  if (!since) return null

  return (
    <Text size="xs" c="dimmed">
      unchanged: {elapsed}s
    </Text>
  )
}

interface SessionCardProps {
  session: SessionInfo
  isMuted: boolean
  isInPool: boolean
  lastChangeTime?: number
  onToggleMute: () => void
  onUnmonitor: () => void
}

export function SessionCard({ session, isMuted, isInPool, lastChangeTime, onToggleMute, onUnmonitor }: SessionCardProps) {
  const color = getPrimaryColor(session.status)

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{
        borderColor: isInPool && !isMuted
          ? 'var(--mantine-color-green-5)'
          : 'var(--mantine-color-violet-6)',
        borderWidth: 2,
        transition: 'all 0.3s ease',
        background: isInPool && !isMuted ? 'var(--mantine-color-green-9)' : undefined
      }}
    >
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Text fw={600} size="lg">{session.name}</Text>
          {isMuted && (
            <Badge size="xs" color="gray" variant="filled">MUTED</Badge>
          )}
          {isInPool && !isMuted && (
            <Badge
              size="sm"
              color="green"
              variant="filled"
              leftSection={<IconVolume size={12} />}
            >
              IN POOL
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          {/* Mute toggle */}
          <ActionIcon
            variant={isMuted ? 'filled' : 'subtle'}
            color={isMuted ? 'gray' : 'default'}
            onClick={onToggleMute}
            title={isMuted ? 'Unmute session' : 'Mute session'}
          >
            {isMuted ? <IconVolumeOff size={18} /> : <IconVolume size={18} />}
          </ActionIcon>
          {/* Unmonitor button */}
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={onUnmonitor}
            title="Remove from monitored"
          >
            <IconX size={18} />
          </ActionIcon>
        </Group>
      </Group>

      <Stack gap="sm">
        {/* Primary Status */}
        <Group>
          <ThemeIcon
            size="lg"
            variant="light"
            color={isMuted ? 'gray' : color}
          >
            {getPrimaryIcon(session.status)}
          </ThemeIcon>
          <div>
            <Text size="sm" c="dimmed">Status</Text>
            <Text fw={500}>{session.status.toUpperCase()}</Text>
          </div>
        </Group>

        {/* Detail */}
        {session.detail && (
          <Text size="sm" c="dimmed">
            {getDetailText(session.detail)}
          </Text>
        )}

        {/* Health Badge + Elapsed Time */}
        <Group justify="space-between">
          <Badge
            variant="light"
            color={getHealthColor(session.health)}
            size="lg"
          >
            {session.health}
          </Badge>
          <ElapsedTime since={lastChangeTime} />
        </Group>
      </Stack>
    </Card>
  )
}
